import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { usePermissions } from '../../auth/permissions';
import { AccessDenied } from '../../routes/AuthRoutes';
import { getUserPermissions, saveUserPermissions } from './userService';
import TableScroll from '../../components/TableScroll';
import CompactBreadcrumb from '../../components/CompactBreadcrumb';
import Toast from '../../components/Toast';
import '../warehouses/warehouses.css';
import './users.css';

export default function UserPermissionsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canWrite } = usePermissions();
  const admin = user?.role === 'Admin';
  const writable = canWrite('Users');
  const [details, setDetails] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const busy = useRef(false);
  useEffect(() => {
    let active = true;
    if (!admin) return undefined;
    setLoading(true); setDetails(null); setError(null); setToast(null);
    getUserPermissions(userId).then(data => {
      if (active) { setDetails(data); setSelected(new Set(data.assignedUserPermissions.map(p => p.id))); }
    }).catch(err => { if (active) { setError(err); setToast({ tone: 'error', message: err.message }); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, admin, attempt]);
  useEffect(() => {
    if (toast?.tone !== 'success') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);
  async function save() {
    if (busy.current || !details || !writable) return;
    busy.current = true; setSaving(true);
    try {
      const data = await saveUserPermissions(userId, [...selected]);
      setDetails(data); setSelected(new Set(data.assignedUserPermissions.map(p => p.id)));
      setToast({ tone: 'success', message: 'User permissions saved successfully.' });
    } catch (err) {
      if (err.status === 403 || err.status === 404) setError(err);
      setToast({ tone: 'error', message: err.message });
    } finally { busy.current = false; setSaving(false); }
  }
  if (!admin || error?.status === 403) return <AccessDenied />;
  const allChecked = Boolean(details?.allPermissions.length) && details.allPermissions.every(p => selected.has(p.id));
  return <section className="wh-page wh-list-page user-permissions-page">
    <Toast toast={toast} onClose={() => setToast(null)} />
    <header className="wh-title user-permissions-heading">
      <div>
        <CompactBreadcrumb parent="Users" current={details ? `${details.user.name} Permissions` : 'User Permissions'} onNavigate={() => navigate('/users')} disabled={saving} />
        <h1>{details ? `${details.user.name} Permissions` : 'User Permissions'}</h1>
      </div>
      {writable && <div className="user-permissions-actions">
        <button type="button" className="user-permissions-button user-permissions-check-all" aria-label="Check All" aria-pressed={allChecked}
          disabled={loading || saving || Boolean(error) || !details?.allPermissions.length}
          onClick={() => setSelected(new Set(allChecked ? [] : details.allPermissions.map(p => p.id)))}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="1" /><path d="m7 12 3 3 7-7" /></svg>
          Assign Check All
        </button>
        {details && !error && <button type="button" className="user-permissions-button user-permissions-save" disabled={saving} onClick={save}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M20 21H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h13l4 4v13a1 1 0 0 1-1 1Z" /><path d="M7 3v6h10V3M7 21v-8h10v8" /></svg>
          {saving ? 'Saving...' : 'Save'}
        </button>}
      </div>}
    </header>
    <div className="wh-panel user-permissions-card">
      <TableScroll><table className="wh-table"><thead><tr><th scope="col">Name</th><th scope="col">Description</th><th scope="col" aria-label="Assign permission">
      </th></tr></thead><tbody>
        {loading || error || !details?.allPermissions.length ? <tr><td colSpan={3} className="users-empty">{loading ? 'Loading permissions...' : error ? <><span role="alert">{error.message}</span> <button type="button" className="wh-form-cancel" onClick={() => setAttempt(value => value + 1)}>Retry</button></> : 'No permissions available.'}</td></tr>
          : details.allPermissions.map(permission => <tr key={permission.id}><td>{permission.code}</td><td>{permission.description || `${permission.code.replace('.', ' ')} Permission`}</td><td><input type="checkbox" aria-label={`Assign ${permission.code}`} checked={selected.has(permission.id)} disabled={!writable || saving}
            onChange={event => { const checked = event.target.checked; setSelected(current => { const next = new Set(current); if (checked) next.add(permission.id); else next.delete(permission.id); return next; }); }} /></td></tr>)}
      </tbody></table></TableScroll>
    </div>
  </section>;
}
